/**
 * A casca das telas de acesso: entrar, recuperar senha e o que vier.
 *
 * Painel de marca escuro à esquerda, sobre foto, com o argumento; cartão claro à direita, com a
 * tarefa. É o layout aprovado no mockup, com a paleta de liderasst.com.br.
 *
 * **Existe para não ter duas cópias do mesmo painel.** As telas de acesso mudam só o texto da
 * esquerda e o conteúdo do cartão; tudo o mais — véu sobre a foto, diagonais, selo do canto,
 * assinatura, e a regra de caber sem rolagem — é igual. Duas cópias divergem no primeiro ajuste
 * que alguém fizer com pressa em uma só.
 *
 * Tudo o que é da marca passa por `branding`: foto, logo e as três cores. O sistema é
 * white-label, e cor escrita direto aqui é cor que o próximo cliente não troca sem mexer no
 * código.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

import branding from '../../config/branding';
import BrandLogo from '../common/BrandLogo';
import {
  comAlfa,
  loginAccent,
  loginNavy,
  LINHA,
  SERIFA,
  VEU,
} from './estilos';

/** O filete teal que abre cada bloco de texto. */
export const Filete: React.FC<{ largura?: number; altura?: number; mb?: number }> = ({
  largura = 38,
  altura = 3,
  mb = 3.5,
}) => <Box sx={{ width: largura, height: altura, mb, bgcolor: loginAccent }} />;

/** O círculo numerado dos passos ("01", "02", "03"). */
export const Numero: React.FC<{ valor: string }> = ({ valor }) => (
  <Box
    sx={{
      display: 'grid',
      placeItems: 'center',
      width: 42,
      height: 42,
      borderRadius: '50%',
      border: `1px solid ${comAlfa(loginAccent, 0.65)}`,
      color: '#19d1c9',
      fontSize: '0.78rem',
      fontWeight: 700,
    }}
  >
    {valor}
  </Box>
);

/** Um item da lista do painel escuro: ícone ou número, título e uma linha de explicação. */
export interface ApoioDaHistoria {
  chave: string;
  /** O ícone ou o número que abre a linha. */
  marca: React.ReactNode;
  titulo: string;
  texto: string;
}

interface Props {
  /** Primeira linha do título, em branco. */
  titulo: string;
  /** Segunda linha, em teal. */
  destaque: string;
  subtitulo: string;
  apoios: readonly ApoioDaHistoria[];
  /** O selo do canto superior direito, em duas linhas. */
  selo: readonly [string, string];
  /** A assinatura do rodapé do painel. `null` deixa o painel sem ela. */
  assinatura?: readonly [string, string] | null;
  /** O conteúdo do cartão. */
  children: React.ReactNode;
}

export const TelaDeAcesso: React.FC<Props> = ({
  titulo,
  destaque,
  subtitulo,
  apoios,
  selo,
  assinatura = ['Pessoas mais saudáveis.', 'Empresas mais fortes.'],
  children,
}) => (
  <Box
    sx={{
      // A tela de acesso não rola: ela ocupa a altura da janela e cabe dentro dela. Quem chega
      // aqui tem uma tarefa só, e ter de rolar para achar o botão é a própria definição de tela
      // mal resolvida. O que faz caber são os `clamp` em `vh` abaixo, que encolhem os espaços
      // antes de encolher o texto.
      height: '100svh',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.65fr) minmax(440px, 0.95fr)' },
      gridTemplateRows: { xs: 'auto 1fr', md: '1fr' },
    }}
  >
    {/* Painel de marca */}
    <Box
      component="section"
      sx={{
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
        px: { xs: 2.5, md: 'clamp(30px, 4vw, 64px)' },
        // No celular este painel vira uma faixa com o logo: empilhar a história inteira em cima
        // do cartão obrigaria a rolar até o botão, que é o que não pode.
        py: { xs: 1.75, md: 'clamp(24px, 4vh, 64px)' },
        bgcolor: loginNavy,
        backgroundImage: branding.loginBackground ? `url(${branding.loginBackground})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: { xs: '65% center', md: 'center' },
        backgroundRepeat: 'no-repeat',
        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: -1,
          inset: 0,
          background: VEU,
        },
        // As diagonais finas que atravessam a foto. Só decoração — por isso ficam atrás de tudo
        // e desaparecem no celular, onde o painel vira uma faixa curta.
        '&::after': {
          content: '""',
          position: 'absolute',
          zIndex: -1,
          inset: '-20% 42% -20% -24%',
          borderRight: `1px solid ${comAlfa(loginAccent, 0.2)}`,
          transform: 'skewX(-18deg)',
          boxShadow: `90px 0 0 -89px ${comAlfa(loginAccent, 0.16)}, 180px 0 0 -179px ${comAlfa(
            loginAccent,
            0.12
          )}`,
          display: { xs: 'none', md: 'block' },
        },
      }}
    >
      <BrandLogo
        variant="light"
        sx={{
          position: 'relative',
          zIndex: 1,
          width: { xs: 132, md: 'clamp(170px, 16vw, 250px)' },
          height: 'auto',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 620,
          display: { xs: 'none', md: 'block' },
          mt: 'clamp(24px, 6vh, 96px)',
        }}
      >
        <Filete />
        <Typography
          component="h1"
          sx={{
            fontFamily: SERIFA,
            fontWeight: 700,
            // O `min` entre largura e altura é o que impede o título de crescer numa janela
            // larga e baixa — que é justamente onde a tela estouraria.
            fontSize: 'clamp(2.1rem, min(4.1vw, 6.4vh), 4.45rem)',
            lineHeight: 1.04,
            letterSpacing: '-0.035em',
          }}
        >
          {titulo}
          <br />
          <Box component="span" sx={{ color: loginAccent }}>
            {destaque}
          </Box>
        </Typography>
        <Typography
          sx={{
            maxWidth: 565,
            mt: 'clamp(12px, 2.4vh, 26px)',
            color: '#d9e3ef',
            fontSize: 'clamp(1rem, min(1.45vw, 2.3vh), 1.35rem)',
            lineHeight: 1.5,
          }}
        >
          {subtitulo}
        </Typography>

        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            gap: 'clamp(14px, 3vh, 29px)',
            mt: 'clamp(20px, 4.5vh, 64px)',
          }}
        >
          {apoios.map((item) => (
            <Box
              key={item.chave}
              sx={{
                display: 'grid',
                gridTemplateColumns: '58px 1fr',
                gap: 2.5,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 48,
                  height: 48,
                  color: loginAccent,
                  '& svg': { fontSize: 'clamp(30px, 4.6vh, 42px)' },
                }}
              >
                {item.marca}
              </Box>
              <Box>
                <Typography
                  component="strong"
                  sx={{ display: 'block', fontFamily: SERIFA, fontSize: '1.08rem', fontWeight: 700 }}
                >
                  {item.titulo}
                </Typography>
                <Typography
                  component="small"
                  sx={{
                    display: 'block',
                    mt: '6px',
                    color: '#cad7e6',
                    fontSize: '0.98rem',
                    lineHeight: 1.35,
                  }}
                >
                  {item.texto}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* A assinatura é a primeira coisa a sair quando a janela é baixa: ela fecha o painel,
          mas não é o que traz ninguém aqui. */}
      {assinatura && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            mt: 'auto',
            pt: 4,
            display: { xs: 'none', lg: 'block' },
            '@media (max-height: 720px)': { display: 'none' },
          }}
        >
          <Filete largura={38} altura={3} mb={2.25} />
          <Typography
            sx={{
              color: '#d9e2ee',
              fontSize: '0.77rem',
              lineHeight: 1.7,
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
            }}
          >
            {assinatura[0]}
            <br />
            {assinatura[1]}
          </Typography>
        </Box>
      )}
    </Box>

    {/* Área de acesso */}
    <Box
      component="section"
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2.5, md: 'clamp(28px, 4.2vw, 70px)' },
        py: 'clamp(16px, 3vh, 64px)',
        // Rolagem só como válvula: numa janela muito baixa o botão continua alcançável em vez
        // de ficar cortado. No tamanho de tela de gente, não dispara.
        overflowY: 'auto',
        background: 'radial-gradient(circle at 65% 28%, #ffffff 0, #f7f9fc 38%, #eef3f8 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 'clamp(16px, 3.5vh, 40px)',
          right: 'clamp(28px, 4.2vw, 70px)',
          display: { xs: 'none', md: 'block' },
          // Em janela baixa o selo some: ele divide a altura com o cartão, e o cartão ganha.
          '@media (max-height: 760px)': { display: 'none' },
          textAlign: 'right',
        }}
      >
        <Box sx={{ width: 28, height: 2, mb: 1.5, ml: 'auto', bgcolor: loginAccent }} />
        <Typography
          sx={{
            color: '#74829c',
            fontSize: '0.69rem',
            fontWeight: 700,
            lineHeight: 1.65,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
          }}
        >
          {selo[0]}
          <br />
          {selo[1]}
        </Typography>
      </Box>

      <Box
        sx={{
          width: 'min(100%, 560px)',
          // O espaçamento vertical cede com a altura da janela; o horizontal não, porque é ele
          // que mantém o título numa linha só.
          py: 'clamp(24px, 4.4vh, 46px)',
          px: { xs: '22px', md: '44px' },
          border: `1px solid ${LINHA}`,
          // Raio e sombra do site (--radius e --shadow de liderasst.com.br).
          borderRadius: '20px',
          bgcolor: 'rgba(255, 255, 255, 0.96)',
          boxShadow: '0 22px 60px rgba(5, 28, 57, 0.14)',
          backdropFilter: 'blur(12px)',
        }}
      >
        {children}
      </Box>
    </Box>
  </Box>
);

export default TelaDeAcesso;
